import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Bank, CheckCircle, WarningCircle, ArrowClockwise, Info,
} from "@phosphor-icons/react";
import client from "../lib/api";

/**
 * QuickBooksConnectCard
 *
 * Drop this component into any Settings-style page. It handles:
 *   - Fetching /api/qbo/status
 *   - Opening the OAuth popup and listening for postMessage back
 *   - Rendering three states: not configured, disconnected, connected
 *   - Disconnect action
 *
 * Optional props:
 *   `compact` (bool) — renders a slim horizontal card for use in a Settings grid
 *   `onStatusChange` (fn) — called with the new status after any change
 */
export default function QuickBooksConnectCard({ compact = false, onStatusChange } = {}) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/qbo/status");
      setStatus(data);
      onStatusChange?.(data);
    } catch {/* silent */}
    finally { setLoading(false); }
  }, [onStatusChange]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onMsg = (e) => {
      if (e.data?.type === "qbo:connected") {
        toast.success(`QuickBooks connected · ${e.data.company_name || e.data.realm_id}`);
        setConnecting(false);
        load();
      } else if (e.data?.type === "qbo:error") {
        toast.error(`QuickBooks connect failed: ${e.data.message || "Unknown error"}`);
        setConnecting(false);
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [load]);

  const connect = async () => {
    setConnecting(true);
    try {
      const { data } = await client.post("/qbo/connect/start");
      const url = data.authorize_url;
      // Center popup on parent monitor
      const w = 600, h = 700;
      const left = window.screenX + (window.outerWidth - w) / 2;
      const top = window.screenY + (window.outerHeight - h) / 2;
      const popup = window.open(url, "qbo-connect",
        `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`);
      if (!popup) {
        toast.error("Please allow popups to connect QuickBooks");
        setConnecting(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start QuickBooks connect");
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect QuickBooks? You'll need to reconnect to sync invoices again.")) return;
    try {
      await client.delete("/qbo/disconnect");
      toast.success("QuickBooks disconnected");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Disconnect failed");
    }
  };

  if (loading) {
    return (
      <div className="card-surface p-4 flex items-center gap-3 animate-pulse" data-testid="qbo-card-loading">
        <div className="w-10 h-10 rounded bg-white/5" />
        <div className="flex-1">
          <div className="h-3 bg-white/5 rounded w-32 mb-2" />
          <div className="h-2 bg-white/5 rounded w-48" />
        </div>
      </div>
    );
  }

  // Unconfigured — QBO env vars missing on backend
  if (status && status.configured === false) {
    return (
      <div className="card-surface p-4 flex items-start gap-3" data-testid="qbo-card-unconfigured">
        <Bank size={24} className="text-neutral-500 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="text-sm font-semibold text-white flex items-center gap-2">
            QuickBooks Online
            <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border border-neutral-500/40 text-neutral-400">
              Not configured
            </span>
          </div>
          <div className="text-xs text-neutral-500 mt-1 flex items-start gap-1.5">
            <Info size={12} className="mt-0.5 flex-shrink-0" />
            <span>An admin needs to set <code className="text-neutral-400 font-mono text-[10px]">QBO_CLIENT_ID</code> in the backend env before pilots can connect.</span>
          </div>
        </div>
      </div>
    );
  }

  const connected = status?.connected === true;

  return (
    <div
      className={`card-surface p-4 ${compact ? "flex items-center gap-3" : ""}`}
      data-testid={connected ? "qbo-card-connected" : "qbo-card-disconnected"}
    >
      <div className={`${compact ? "" : "flex items-start justify-between gap-4 mb-3"}`}>
        <div className="flex items-start gap-3 flex-1">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
            connected ? "bg-emerald-500/15 text-emerald-400" : "bg-white/5 text-neutral-500"
          }`}>
            <Bank size={22} weight={connected ? "fill" : "regular"} />
          </div>
          <div className="flex-1">
            <div className="text-sm font-semibold text-white flex items-center gap-2">
              QuickBooks Online
              {connected ? (
                <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 inline-flex items-center gap-1">
                  <CheckCircle size={10} weight="fill" /> Connected
                </span>
              ) : status?.status === "expired" ? (
                <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-amber-500/15 border border-amber-500/40 text-amber-300 inline-flex items-center gap-1">
                  <WarningCircle size={10} weight="fill" /> Expired
                </span>
              ) : (
                <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border border-white/15 text-neutral-500">
                  Not connected
                </span>
              )}
              {status?.environment === "sandbox" && (
                <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border border-amber-500/30 text-amber-300 bg-amber-500/5">
                  Sandbox
                </span>
              )}
            </div>
            {connected ? (
              <div className="text-xs text-neutral-400 mt-1">
                {status.company_name || `Realm ${status.realm_id}`}
                {status.last_refreshed_at && (
                  <span className="text-neutral-600 ml-2">
                    · refreshed {new Date(status.last_refreshed_at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}
                  </span>
                )}
              </div>
            ) : (
              <div className="text-xs text-neutral-500 mt-1 max-w-md">
                Sync invoices + payroll journal entries automatically. One-click OAuth — no CSV exports.
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={`${compact ? "ml-auto" : "flex items-center justify-end gap-2"}`}>
        {connected ? (
          <>
            <button
              onClick={load}
              className="text-xs rounded-full px-3 py-1.5 border border-white/10 text-neutral-400 hover:text-white hover:border-white/25 transition-colors inline-flex items-center gap-1.5"
              data-testid="qbo-refresh-status"
            >
              <ArrowClockwise size={11} /> Refresh
            </button>
            <button
              onClick={disconnect}
              className="text-xs rounded-full px-3 py-1.5 border border-white/10 text-neutral-500 hover:text-rose-300 hover:border-rose-500/40 transition-colors"
              data-testid="qbo-disconnect-btn"
            >
              Disconnect
            </button>
          </>
        ) : (
          <button
            onClick={connect}
            disabled={connecting}
            data-testid="qbo-connect-btn"
            className="btn-primary rounded-full px-5 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-60"
          >
            {connecting ? (
              <>
                <div className="w-3 h-3 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                Waiting for QuickBooks...
              </>
            ) : (
              <>
                <Bank size={13} weight="fill" /> Connect QuickBooks
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}
